import { useCallback, useEffect, type RefObject } from "react";

interface IUseBuilderToolbarDismissOptions {
  isOpen: boolean;
  onClose: () => void;
  railRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  shouldIgnoreTarget?: (target: Node) => boolean;
  disabled?: boolean;
  dismissOnEscape?: boolean;
}

export function useBuilderToolbarDismiss(options: IUseBuilderToolbarDismissOptions): void {
  const { isOpen, onClose, railRef, panelRef, shouldIgnoreTarget, disabled, dismissOnEscape = true } =
    options;

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (
        disabled ||
        !isOpen ||
        !(e.target instanceof Node) ||
        railRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target) ||
        shouldIgnoreTarget?.(e.target)
      ) {
        return;
      }

      onClose();
    },
    [disabled, isOpen, onClose, panelRef, railRef, shouldIgnoreTarget],
  );

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (disabled || !dismissOnEscape || !isOpen || e.key !== "Escape") {
        return;
      }

      onClose();
    },
    [disabled, dismissOnEscape, isOpen, onClose],
  );

  useEffect(() => {
    document.addEventListener("pointerdown", handlePointerDown, { capture: true });

    return () => document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
  }, [handlePointerDown]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);
}
